import { inject, Injectable } from '@angular/core';
import {
  SessionTimeoutDialogComponent,
  SessionDialogData
} from '@components/session-timeout-dialog/session-timeout-dialog.component';
import { ConsentFormService } from '@consent';
import { STORAGE } from '@constants/storage.constant';
import { StorageService } from '@services/storage.service';
import { UserStateService } from '@services/user-state.service';
import { DialogService } from 'primeng/dynamicdialog';
import { Subscription } from 'rxjs';

// Dialog priorities. A higher-priority dialog supersedes a lower-priority one that
// is already open, so a dismissable rate-limit dialog can't swallow a forced-logout.
const DIALOG_PRIORITY = {
  RATE_LIMIT: 1,
  FORCED_LOGOUT: 2
} as const;

const DIALOG_OPTIONS = {
  header: '',
  width: '400px',
  modal: true,
  styleClass: 'no-dialog-header',
  maskStyleClass: 'backdrop-blur-md',
  closable: false,
  closeOnEscape: false,
  dismissableMask: false,
  draggable: false,
  resizable: false,
  baseZIndex: 1000,
  transitionOptions: '120ms'
} as const;

@Injectable({ providedIn: 'root' })
export class SessionTimeoutService {
  private dialogOpen = false;
  private dialogPriority = 0;
  private dialogCloseSub?: Subscription;

  private readonly storageService = inject(StorageService);
  private readonly userStateService = inject(UserStateService);
  private readonly dialogService = inject(DialogService);
  private readonly consentFormService = inject(ConsentFormService);

  /** Backend enforces idle timeout; frontend only reacts to backend session states. */
  initializeSessionTimeout(): void {
    return;
  }

  /**
   * Single reusable teardown for every "session is gone" path (standard 401,
   * token-theft 401, explicit logout). It (a) closes ALL open dynamic dialogs —
   * so a consent (or any) modal can't linger over the login screen — and (b)
   * resets the consent singleton so the next login re-prompts from a clean
   * state. Idempotent: safe to call from multiple paths / multiple times.
   */
  tearDownDialogsAndConsent(): void {
    this.closeOpenDialogs();
    this.consentFormService.resetSession();
  }

  /**
   * Ends the session immediately: clears the in-memory token/user state
   * (UserStateService — the actual source of truth since AS-08/AS-09) plus
   * any leftover session-related local storage keys. Called by the
   * interceptor on 440 / token-theft, ahead of the redirect to /logout.
   */
  clearAuthStorage(): void {
    this.userStateService.clearUser();
    [
      STORAGE.LOGIN_TOKEN,
      STORAGE.SESSION_ID,
      STORAGE.REFRESH_TOKEN,
      STORAGE.USER_DATA,
      STORAGE.USER_ROLES,
      STORAGE.USER_ROLES_PERMISSIONS,
      STORAGE.FULL_NAME
    ].forEach((key) => this.storageService.removeFromLocal(key));
  }

  /** Shows the 429 "Too Many Requests" dialog. Closes without redirecting so the user can retry. */
  showRateLimitedDialog(backendMessage?: string): void {
    this.showForcedLogoutDialog(
      {
        icon: 'pi pi-exclamation-circle',
        iconBg: 'bg-orange-100',
        iconColor: 'text-orange-500',
        heading: 'Request Limit Reached',
        message: backendMessage ?? 'Too many requests. Please try again later.',
        buttonLabel: 'OK',
        redirectTo: null
      },
      DIALOG_PRIORITY.RATE_LIMIT
    );
  }

  /** Shows the 440 "Session Terminated" dialog with the backend-provided message. */
  showSessionTerminatedDialog(backendMessage?: string): void {
    // Forced logout: reset consent state so re-login re-prompts cleanly. (Rate
    // limiting keeps the user logged in, so it must NOT reset — hence this lives
    // here, not in the shared showForcedLogoutDialog.) showForcedLogoutDialog
    // closes the open consent dialog itself via closeOpenDialogs().
    this.consentFormService.resetSession();
    this.showForcedLogoutDialog({
      icon: 'pi pi-ban',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-500',
      heading: 'Session Terminated',
      message:
        backendMessage ??
        'Your session was terminated by an administrator. Please sign in again.',
      buttonLabel: 'Sign in'
    });
  }

  /**
   * Opens the session-state dialog with the given data.
   * Guards against duplicate dialogs when multiple HTTP requests fail simultaneously.
   * A higher-priority dialog supersedes a lower-priority one already on screen, so a
   * dismissable rate-limit dialog never swallows a forced-logout/session-terminated one.
   */
  showForcedLogoutDialog(
    data: SessionDialogData = {},
    priority: number = DIALOG_PRIORITY.FORCED_LOGOUT
  ): void {
    // An equal-or-higher priority dialog is already showing — leave it be.
    if (this.dialogOpen && priority <= this.dialogPriority) return;

    // Superseding a lower-priority dialog: detach its close handler first so it
    // doesn't reset our state when closeOpenDialogs() tears it down.
    this.dialogCloseSub?.unsubscribe();
    this.closeOpenDialogs();

    this.dialogOpen = true;
    this.dialogPriority = priority;
    const ref = this.dialogService.open(SessionTimeoutDialogComponent, {
      ...DIALOG_OPTIONS,
      data
    });
    this.dialogCloseSub = ref.onClose.subscribe(() => {
      this.dialogOpen = false;
      this.dialogPriority = 0;
    });
  }

  cleanup(): void {
    return;
  }

  isServiceInitialized(): boolean {
    return false;
  }

  // ── Private ─────────────────────────────────────────────────────────────────

  /** Closes all open PrimeNG dynamic dialogs via the service API (no DOM hacks). */
  private closeOpenDialogs(): void {
    this.dialogService.dialogComponentRefMap.forEach((_, ref) => ref.close());
    document.body.classList.remove('p-overflow-hidden');
  }
}
